import {
  Suspense, lazy, useCallback, useEffect, useState,
} from 'react';
import {
  BrowserRouter,
  Route,
  Routes,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';

import { ModalContainer, Reoverlay } from 'reoverlay';
import 'bootstrap/dist/css/bootstrap.min.css';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { PageRoute } from './utils/routes';
import { configurationFromDomain } from './utils/requestUtils';
import { FranchiseWithLang } from './types/franchise';
import {
  setAvailableCountryCodes, setFranchises, setPermissions, setUserInfo,
} from './reducers/userInfoSlice';
import { checkToken } from './api/tokenRequests';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import CloseModalsOnNavigate from './components/CloseModalsOnNavigate/CloseModalsOnNavigate';
import { navigateToSpecificPage } from './utils/routeUtils';
import CommandMenu from './components/CommandMenu/CommandMenu';
import { useResponsiveBaseFont } from './hooks/useResponsiveFont';

const LoginPage = lazy(() => import('./pages/loginPage/LoginPage'));
const ResetPasswordPage = lazy(() => import('./pages/loginPage/ResetPasswordPage'));
const PrivateApp = lazy(() => import('./PrivateApp'));

const PUBLIC_PAGE_ROUTES: PageRoute[] = [
  {
    path: '/login',
    main: () => <LoginPage />,
    isPrivate: false,
  },
  {
    path: '/reset-password',
    main: () => <ResetPasswordPage />,
    isPrivate: false,
  },
  {
    path: '*',
    main: () => <LoginPage />,
    isPrivate: false,
  },
];

const PublicApp = (): JSX.Element => (
  <Routes>
    {PUBLIC_PAGE_ROUTES
      .map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={(
            <route.main />
              )}
        />
      ))}
  </Routes>
);

const queryClient = new QueryClient();
let timer: NodeJS.Timeout;

const App = (): JSX.Element => {
  const permissions = useAppSelector((state) => state.userInfo.permissions);

  const [isLoading, setIsLoading] = useState(true);
  const queryParameters = new URLSearchParams(window.location.search);
  const queryToken = queryParameters.get('token');
  const queryFr = queryParameters.get('fr');
  const cmsInfo = queryParameters.get('cmsinfo');
  const currentPath = window.location.pathname;
  const isResetPasswordRoute = currentPath === '/reset-password';

  const token = !isResetPasswordRoute
    ? (queryToken || localStorage.getItem('authToken'))
    : localStorage.getItem('authToken');

  const userConfiguration = localStorage.getItem('userConfiguration');
  const configuration = userConfiguration || configurationFromDomain(window.location.hostname);
  const renderSideBar = !(queryToken || localStorage.getItem('queryTokenSession'));
  const [tokenCheck, setTokenCheck] = useState<boolean>(!!queryToken && !isResetPasswordRoute);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const dispatch = useAppDispatch();

  const shouldValidateToken: boolean = !isResetPasswordRoute
    && !!token
    && ((configuration && typeof configuration === 'string') || !!queryFr);

  const isWBF = !!queryToken && !!queryFr && !isResetPasswordRoute;

  const validateToken = useCallback(async () => {
    if (shouldValidateToken && token) {
      checkToken(token, configuration, queryFr)
        .then((response) => {
          setTokenCheck(false);
          if (response && response.Success) {
            const buffer = response.Data.TokenExpirationTime > 30000
              ? 30000 : response.Data.TokenExpirationTime * 0.90;
            const tokenExpirationTime = response.Data.TokenExpirationTime - buffer;
            setIsAuthenticated(true);
            localStorage.setItem('authToken', token);
            if (response.Data.Configuration) localStorage.setItem('userConfiguration', response.Data.Configuration);
            localStorage.setItem('tokenExpiry', new Date().toString());
            if (queryToken) {
              localStorage.setItem('queryTokenSession', 'true');
            }
            const franchises: FranchiseWithLang[] = response.Data.Franchises;
            if (franchises && franchises.length) {
              localStorage.setItem(
                'userFranchises',
                JSON.stringify(response.Data.Franchises),
              );
              localStorage.setItem(
                'managerCurrencyCode',
                response.Data.ManagerCurrencyCode,
              );
              localStorage.setItem(
                'managerCurrencyId',
                response.Data.ManagerCurrencyId,
              );
              dispatch(setFranchises(franchises));
            }

            if (response && response.Data) {
              const userInfo = `${response.Data.FirstName} ${response.Data.LastName}`;
              dispatch(setUserInfo({
                username: userInfo,
                currencyCode: response.Data.ManagerCurrencyCode,
                currencyId: response.Data.ManagerCurrencyId,
                oddsFormat: response.Data.OddsFormat,
                autoCalculateOdds: response.Data.AutoCalculateOdds,
                configuration: response.Data.Configuration,
                managerId: response.Data.ManagerId,
                ManagerUuid: response.Data.ManagerUuid,
                IsGlobal: response.Data.IsGlobal,
                Routes: response.Data.Routes,
              }));
            }

            if (response.Data.CountryCodes) {
              const countryCodes = response.Data.CountryCodes.map((countryCode) => countryCode.code);
              dispatch(setAvailableCountryCodes(countryCodes));
              localStorage.setItem(
                'availableCountryCodes',
                countryCodes.toString(),
              );
            }

            if (response.Data.Functions) {
              localStorage.setItem('userPermissions', response.Data.Functions.toString());
              dispatch(setPermissions(response.Data.Functions));
            }

            timer = setTimeout(() => {
              validateToken();
            }, tokenExpirationTime);

            if (cmsInfo && queryFr && queryToken) navigateToSpecificPage(cmsInfo, queryFr, queryToken);
          } else {
            localStorage.removeItem('authToken');
            localStorage.removeItem('tokenExpiry');
            localStorage.removeItem('userFranchises');
            localStorage.removeItem('userConfiguration');
            localStorage.removeItem('queryTokenSession');
            setIsAuthenticated(false);
            toast.error('Your token has expired');
            window.clearTimeout(timer);
            Reoverlay.hideModal();
            if (queryFr && queryToken) {
              window.top?.postMessage({ func: 'refreshWBFClient' }, '*');
            } else {
              window.location.href = '/login';
            }
          }
        })
        .catch(() => {
          localStorage.removeItem('authToken');
          localStorage.removeItem('tokenExpiry');
          localStorage.removeItem('userFranchises');
          localStorage.removeItem('userConfiguration');
          localStorage.removeItem('queryTokenSession');
          setIsAuthenticated(false);
          toast.error('Your token has expired');
          window.clearTimeout(timer);
          Reoverlay.hideModal();

          if (queryFr && queryToken) {
            window.top?.postMessage({ func: 'refreshWBFClient' }, '*');
          } else {
            window.location.href = '/login';
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [configuration, queryFr, token, queryToken, shouldValidateToken, cmsInfo, dispatch]);

  useEffect(() => {
    if (isResetPasswordRoute) {
      setIsLoading(false);
      setIsAuthenticated(false);
      return;
    }

    if (shouldValidateToken) {
      validateToken();
    } else {
      setIsLoading(false);
      setIsAuthenticated(false);
    }
  }, [shouldValidateToken, validateToken, isResetPasswordRoute]);

  useResponsiveBaseFont();

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <CloseModalsOnNavigate />
        <Suspense fallback={<LoadingSpinner />}>
          { isLoading && <LoadingSpinner />}
          { !isLoading && (isAuthenticated
            ? <PrivateApp permissions={permissions} isLoading={isLoading} tokenCheck={tokenCheck} renderSideBar={renderSideBar} />
            : (
              isWBF ? <Routes /> : <PublicApp />
            ))}
          <ModalContainer />
        </Suspense>
        <CommandMenu />
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
